import { Box, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';

import { semanticColors } from '@/theme/tokens';

import type { NarrationEvent } from './NarrationTypes';
import ToolCategoryIcon, { toolCategoryLabelKey } from './ToolCategoryIcon';

/**
 * The research trail for one turn: the steps taken, and the reasoning between
 * them.
 *
 * Tool steps and reasoning are visually distinct on purpose. A step is a
 * discrete, completed action, so it reads as a checklist row with a badge. The
 * reasoning is continuous prose, so it reads as prose — indented under the step
 * that prompted it, no badge competing for the eye. Rendering both as identical
 * italic lines (which is what shipping thinking on the shared narration channel
 * did before this) made the reasoning look like a malfunctioning status line.
 */

interface NarrationTimelineProps {
  events: NarrationEvent[];
  /** `false` renders the after-the-fact view: no live region, dimmer. */
  live?: boolean;
}

export default function NarrationTimeline({ events, live = true }: NarrationTimelineProps) {
  const { t } = useTranslation('chat');

  if (!events.length) return null;

  return (
    <Box
      // Live narration is an assistive-tech announcement; the retained copy is
      // static content the reader chose to open, so it must not re-announce.
      {...(live ? { role: 'status', 'aria-live': 'polite', 'aria-atomic': false } : {})}
      sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}
    >
      {events.map((event, i) => {
        if (event.kind === 'thinking') {
          const text = event.text.trim();
          if (!text) return null;
          return (
            <Typography
              key={i}
              // Held out of the live region while streaming. Reasoning arrives
              // token by token, so a polite live region would re-announce a
              // growing paragraph on every delta — unusable. Tool steps are
              // discrete and stay announced, which is the useful signal, and the
              // full reasoning is readable in `ReasoningDisclosure` afterwards,
              // where it is static and `live` is false.
              aria-hidden={live ? 'true' : undefined}
              sx={{
                // Indented past the badge column so reasoning reads as
                // subordinate to the step above it rather than a sibling.
                pl: 3.5,
                fontSize: 13,
                lineHeight: 1.6,
                color: semanticColors.textCaption,
                fontStyle: 'italic',
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
              }}
            >
              {text}
            </Typography>
          );
        }

        if (event.kind === 'transition') {
          return (
            <Typography
              key={i}
              sx={{
                pl: 3.5,
                fontSize: 13,
                lineHeight: 1.6,
                color: semanticColors.textCaption,
                overflowWrap: 'anywhere',
              }}
            >
              {event.text}
            </Typography>
          );
        }

        const category = event.toolCategory ?? 'generic';
        return (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <ToolCategoryIcon category={category} size={22} />
            <Typography
              sx={{
                fontSize: 13,
                lineHeight: 1.5,
                color: semanticColors.textCaption,
                overflowWrap: 'anywhere',
              }}
            >
              {/* The category is named for screen readers only — sighted users
                  get it from the badge, and repeating it in the visible line
                  would be redundant with the status text.

                  MUI's own ``visuallyHidden`` rather than a hand-rolled clip:
                  in ``sx``, ``width: 1`` / ``height: 1`` are the sizing
                  system's *ratios*, so they resolve to ``100%``, not one pixel.
                  Clipped, that box paints nothing — but it still occupies
                  layout, and an absolutely-positioned 100%-tall box inside the
                  message list added its own height again to the chat scroll
                  container's scrollHeight. The reader could then scroll roughly
                  a screenful past the last message, and the sticky chat input
                  (whose containing block ends with the real content) rode up
                  and off the top on the way. */}
              <Box component="span" sx={visuallyHidden}>
                {t(toolCategoryLabelKey(category))}:{' '}
              </Box>
              {event.text}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
